import { AxiosInstance } from 'axios';
import { RESULTS_PER_PAGE } from 'constants/fieldData';
import { sortFields } from 'constants/sortFields';

import useApiRequest from 'hooks/useApiRequest';
import useAsync from 'hooks/useAsync';
import { fixVenue, Venue as VenueWithSpaces } from 'hooks/useVenue';
import { get } from 'lodash/object';
import { useCallback, useMemo, useState } from 'react';

type Venue = Omit<VenueWithSpaces, 'spaces'>;
type LatLng = Venue['latLng'];

// `null` is permitted alongside `undefined` because the search state uses null
// for "unset" (see SearchFilters), and both are skipped when building params.
// `sort` is a plain string rather than `keyof typeof sortFields`: the state's
// default is 'Name', which is not a sort field, and getParams looks it up
// through `get(..., default)` precisely to tolerate that.
export interface Filters {
  latLng?: LatLng | null;
  // The search form emits the select's string value; getParams tolerates both.
  distance?: number | string | null;
  location?: string | null;
  sort?: string;
  excludedVenueIds?: number[];
  dir?: 'asc' | 'desc';
}

export interface Options<F = Filters> {
  page?: number;
  perPage?: number;
  viewType?: string;
  filters?: F;
}

export interface VenueSearchData<V extends Venue = Venue> {
  venues: V[];
  alternativeVenues: V[];
  totalVenues: number;
  unfilteredTotalVenues: number;
}

export const getSearchData = (
  venues: Venue[] = [],
  totalVenues: number = venues.length
): VenueSearchData => ({
  venues,
  alternativeVenues: [],
  totalVenues,
  unfilteredTotalVenues: totalVenues,
});

export const emptyData = getSearchData();

export const fixData = <Data extends VenueSearchData = VenueSearchData>(
  data: Data
): Data => ({
  ...data,
  venues: data.venues.map(fixVenue),
  alternativeVenues: (data.alternativeVenues ?? []).map(fixVenue),
});

const fixLatLng = ({ lat, lng }: NonNullable<LatLng>): LatLng => ({
  lat,
  lng,
});

export const getParams = ({
  filters: {
    sort = 'Name' as keyof typeof sortFields,
    distance = 0,
    latLng,
    location,
    ...rest
  } = {},
  viewType = '',
  page = 1,
  perPage = RESULTS_PER_PAGE,
}: Options = {}) => ({
  ...rest,
  ...(latLng ? fixLatLng(latLng) : { ...(location ? { location } : {}) }),
  ...(distance && Number(distance) > 0 ? { distance } : {}),
  ...(viewType === 'map'
    ? { perPage: 9999, mapView: viewType === 'map', includeSpaces: false }
    : {
        sort: get(sortFields, `${sort}.field`, 'name'),
        dir: get(sortFields, `${sort}.dir`, 'asc'),
        perPage,
        page,
      }),
});

export const getVenueSearch = (api: AxiosInstance, options: Options = {}) =>
  api
    .get<VenueSearchData>('/v1/venues/search', { params: getParams(options) })
    .then(({ data }) => fixData(data));

export const paginateData =
  <Data extends VenueSearchData = VenueSearchData>(
    nextData: Data,
    page: number = 1
  ) =>
  (prevData: Data): Data =>
    page > 1
      ? {
          ...prevData,
          venues: [...prevData.venues, ...nextData.venues],
          alternativeVenues: [
            ...prevData.alternativeVenues,
            ...nextData.alternativeVenues,
          ],
        }
      : nextData;

const useVenueSearch = (options: Options = {}) => {
  const [data, setData] = useState<VenueSearchData | undefined>();

  const apiRequest = useApiRequest();

  const { page = 1 } = options;

  const optionsJson = useMemo(() => JSON.stringify(options), [options]);

  const fetchData = useCallback(async () => {
    const nextData = await getVenueSearch(apiRequest, JSON.parse(optionsJson));

    setData(
      paginateData(nextData, page) as (
        prevData: VenueSearchData | undefined
      ) => VenueSearchData
    );
  }, [page, apiRequest, optionsJson]);

  const { loading, error, refresh } = useAsync(fetchData);

  return { data, loading, error, refresh };
};

export default useVenueSearch;
